/**
 * Router-compat shim — bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useSearch as tsSearch,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import {
  useMemo,
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext,
  forwardRef,
  Children,
  isValidElement,
  type ComponentProps,
  type ReactNode,
  type ReactElement,
} from "react";

// ---------- test-only memory router context ----------
// MemoryRouter/Routes/Route exist ONLY for legacy vitest suites that mount
// components with react-router-dom style test harnesses. Production code
// always runs inside the real TanStack router; this context stays null there.

type MemoryLocation = { pathname: string; search: string; hash: string; state: unknown; key: string };
type MemoryCtxValue = {
  location: MemoryLocation;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
};
const MemoryRouterContext = createContext<MemoryCtxValue | null>(null);
const CompatParamsContext = createContext<Record<string, string> | null>(null);

// ---------- shared URL parsing ----------

function parseTo(to: string): {
  pathname: string;
  search?: Record<string, string> | undefined;
  hash?: string | undefined;
} {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = beforeHash!.split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? Object.fromEntries(new URLSearchParams(searchStr)) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  // Whether we're inside the test-only MemoryRouter is constant for the
  // lifetime of a component instance, so hook order stays stable.
  const mem = useContext(MemoryRouterContext);
  if (mem) {
    return ((to: string | number, options?: NavigateOptions) => {
      if (typeof to === "number") return;
      mem.navigate(to, options?.replace !== undefined ? { replace: options.replace } : {});
    }) as NavigateFn;
  }
  /* eslint-disable react-hooks/rules-of-hooks */
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    tsNav({
      to: pathname,
      search: search as never,
      hash: hash!,
      state: options?.state as never,
      replace: options?.replace!,
    });
  }, [tsNav, router]) as NavigateFn;
  /* eslint-enable react-hooks/rules-of-hooks */
}

// ---------- useLocation ----------

export function useLocation() {
  const mem = useContext(MemoryRouterContext);
  if (mem) return mem.location;
  /* eslint-disable react-hooks/rules-of-hooks */
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
  /* eslint-enable react-hooks/rules-of-hooks */
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  const compat = useContext(CompatParamsContext);
  const mem = useContext(MemoryRouterContext);
  if (mem) return (compat ?? {}) as T;
  /* eslint-disable react-hooks/rules-of-hooks */
  return tsParams({ strict: false } as never) as T;
  /* eslint-enable react-hooks/rules-of-hooks */
}


// ---------- useSearchParams (@/lib/router-compat compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const mem = useContext(MemoryRouterContext);
  if (mem) {
    const params = new URLSearchParams(mem.location.search);
    const setParams = (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      const next =
        typeof init === "function"
          ? init(new URLSearchParams(mem.location.search))
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const qs = next.toString();
      mem.navigate(mem.location.pathname + (qs ? `?${qs}` : ""), opts?.replace !== undefined ? { replace: opts.replace } : {});
    };
    return [params, setParams];
  }
  /* eslint-disable react-hooks/rules-of-hooks */
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => { searchObj[k] = v; });
      nav({ to: live.pathname, search: searchObj as never, replace: opts?.replace! });
    },
    [nav, router],
  );
  return [params, setParams];
  /* eslint-enable react-hooks/rules-of-hooks */
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const mem = useContext(MemoryRouterContext);
  if (mem) {
    return (
      <a
        ref={ref}
        href={to}
        onClick={(e) => {
          e.preventDefault();
          mem.navigate(to, replace !== undefined ? { replace } : {});
        }}
        {...((rest ?? {}) as Record<string, unknown>)}
      >
        {children}
      </a>
    );
  }
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      {...(hash !== undefined ? { hash } : {})}
      {...(replace !== undefined ? { replace } : {})}
      state={state as never}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const mem = useContext(MemoryRouterContext);
  useEffect(() => {
    if (mem) mem.navigate(to, replace !== undefined ? { replace } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mem, to, replace]);
  if (mem) return null;
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSNavigate
      to={pathname as never}
      search={search as never}
      {...(hash !== undefined ? { hash } : {})}
      {...(replace !== undefined ? { replace } : {})}
      state={state as never}
    />
  );
}

// ---------- Outlet ----------

export const Outlet = TSOutlet;

// ---------- NavLink (minimal) ----------

export const NavLink = Link;

// ---------- Test-only MemoryRouter / Routes / Route ----------
// Legacy vitest suites mount pages with react-router-dom style harnesses.
// This is a minimal synchronous in-memory router: it never touches the
// TanStack router and exists purely so those suites keep running.

export function MemoryRouter({
  children,
  initialEntries = ["/"],
}: {
  children?: ReactNode;
  initialEntries?: string[];
}) {
  const [entries, setEntries] = useState<string[]>(() => [...initialEntries]);
  const current = entries[entries.length - 1] ?? "/";
  const navigate = useCallback((to: string, opts?: { replace?: boolean }) => {
    setEntries((prev) => (opts?.replace ? [...prev.slice(0, -1), to] : [...prev, to]));
  }, []);
  const value = useMemo<MemoryCtxValue>(() => {
    const [beforeHash = "", hashStr] = current.split("#");
    const [path = "/", searchStr] = beforeHash.split("?");
    return {
      location: {
        pathname: path || "/",
        search: searchStr ? `?${searchStr}` : "",
        hash: hashStr ? `#${hashStr}` : "",
        state: null,
        key: current,
      },
      navigate,
    };
  }, [current, navigate]);
  return <MemoryRouterContext.Provider value={value}>{children}</MemoryRouterContext.Provider>;
}

function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const pp = pattern.split("/").filter(Boolean);
  const sp = pathname.split("/").filter(Boolean);
  const params: Record<string, string> = {};
  for (let i = 0; i < pp.length; i++) {
    const seg = pp[i]!;
    if (seg === "*") {
      params["*"] = sp.slice(i).join("/");
      return params;
    }
    if (i >= sp.length) return null;
    if (seg.startsWith(":")) {
      params[seg.slice(1)] = decodeURIComponent(sp[i]!);
    } else if (seg !== sp[i]) {
      return null;
    }
  }
  return pp.length === sp.length ? params : null;
}

export function Route(_props: { path: string; element: ReactNode }): ReactElement | null {
  // Rendered only as a child descriptor of <Routes>; never mounts directly.
  return null;
}

export function Routes({ children }: { children?: ReactNode }) {
  const mem = useContext(MemoryRouterContext);
  const pathname = mem?.location.pathname ?? "/";
  let matched: { element: ReactNode; params: Record<string, string> } | null = null;
  Children.forEach(children, (child) => {
    if (matched || !isValidElement(child)) return;
    const props = child.props as { path?: string; element?: ReactNode };
    if (typeof props.path !== "string") return;
    const params = matchPath(props.path, pathname);
    if (params) matched = { element: props.element ?? null, params };
  });
  if (!matched) return null;
  const { element, params } = matched as { element: ReactNode; params: Record<string, string> };
  return <CompatParamsContext.Provider value={params}>{element}</CompatParamsContext.Provider>;
}
